import { Button, Card } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Circle, styled, Text, View } from "tamagui";

import { api } from "~/utils/api";
import { dateToDDMMYY } from "~/utils/date-to-readable";

interface ResearchStepsBase {
  finished: number;
  total: number;
}

export const ResearchStep: React.FC<{
  researchId: string;
  totalUsers: number;
  isActiveResearch: boolean;
}> = ({ researchId, totalUsers, isActiveResearch }) => {
  const { t } = useTranslation("research");
  const apiUtils = api.useUtils();

  const [data] = api.research.slices.useSuspenseQuery({
    researchId,
    limit: 100,
    offset: 0,
  });

  const { mutate } = api.research.completeLatestSlice.useMutation({
    onSuccess: () => apiUtils.research.slices.invalidate(),
  });

  return (
    <>
      {data.map(({ createdAt, status, id, finishedAt, finishedCount }, i) => (
        <Card
          key={id}
          width={252}
          justifyContent="space-between"
          flex={1}
          paddingHorizontal={"$2"}
          paddingVertical={"$3"}
        >
          <View gap={"$1.3"} justifyContent="center" alignItems="center">
            <Text
              data-id={`research-step-title`}
              fontFamily={"$body"}
              fontWeight={"$6"}
              fontSize={"$6"}
              lineHeight={"$6"}
              textAlign={"center"}
              color={status === "completed" ? "$gray-600" : "$gray-primary-900"}
            >
              Етап {i + 1}
            </Text>
            <ResearchStepStats
              active={status !== "completed"}
              finished={finishedCount}
              total={totalUsers}
            />
          </View>

          <View>
            <Text
              data-id={`research-step-startedAt`}
              fontFamily={"$body"}
              fontWeight={"$4"}
              fontSize={"$1"}
              lineHeight={"$1"}
              color={"$gray-600"}
              textAlign={"center"}
            >
              {t(
                "research-list-page.research-card.research-step-card.started-at",
                {
                  date: dateToDDMMYY(createdAt),
                },
              )}
            </Text>
            {finishedAt && (
              <Text
                data-id={`research-step-finishedAt`}
                fontFamily={"$body"}
                fontWeight={"$4"}
                fontSize={"$1"}
                lineHeight={"$1"}
                color={"$gray-600"}
                textAlign={"center"}
              >
                {t(
                  "research-list-page.research-card.research-step-card.finished-at",
                  {
                    date: dateToDDMMYY(finishedAt),
                  },
                )}
              </Text>
            )}
          </View>

          <Button
            data-id={`research-step-button-${status === "completed" ? "finished" : "finish"}`}
            height={"$5.3"}
            variant={"primary"}
            disabled={status === "completed"}
            size="sm"
            rounded="lg"
            width={"100%"}
            onPress={() => mutate({ researchId })}
          >
            {status === "completed"
              ? t(
                  "research-list-page.research-card.research-step-card.button-state.finished",
                )
              : t(
                  "research-list-page.research-card.research-step-card.button-state.finish",
                )}
          </Button>
        </Card>
      ))}
      {isActiveResearch && <CreateNewResearchStep researchId={researchId} />}
    </>
  );
};

const CreateNewResearchStep: React.FC<{ researchId: string }> = ({
  researchId,
}) => {
  const apiUtils = api.useUtils();
  const { mutate } = api.research.createSlice.useMutation({
    onSuccess: () => {
      void apiUtils.research.slices.invalidate();
      void apiUtils.research.reseachHasSlices.invalidate();
    },
  });

  return (
    <Card
      width={252}
      justifyContent="space-between"
      flex={1}
      paddingHorizontal={"$2"}
      paddingVertical={"$3"}
    >
      <View gap={"$1.3"} justifyContent="center" alignItems="center">
        <Text
          fontFamily={"$body"}
          fontWeight={"$6"}
          fontSize={"$6"}
          lineHeight={"$6"}
          textAlign={"center"}
          color={"$gray-primary-900"}
        >
          Новий етап
        </Text>
        <Circle height={40} width={40} backgroundColor={"$gray-100"}>
          <Text
            fontFamily={"$body"}
            fontWeight={"$5"}
            fontSize={"$5"}
            lineHeight={"$5"}
            textAlign={"center"}
            color={"$gray-primary-900"}
          >
            +
          </Text>
        </Circle>
      </View>

      <Button
        data-id={`research-step-button-create-new`}
        height={"$5.3"}
        variant={"outlined"}
        size="sm"
        rounded="lg"
        width={"100%"}
        onPress={() => mutate({ researchId })}
      >
        + Створити етап
      </Button>
    </Card>
  );
};

const ResearchStepStatsWrapper = styled(View, {
  name: "ResearchStepStatsWrapper",
  paddingVertical: "$1.4",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  borderRadius: "$5",

  variants: {
    active: {
      true: {
        backgroundColor: "$teal-50",
      },
      false: {
        backgroundColor: "$gray-50",
      },
    },
  } as const,
  defaultVariants: {
    active: false,
  },
});

const ResearchStepStatsValue = styled(Text, {
  name: "ResearchStepStatsValue",
  variants: {
    active: {
      true: {
        color: "$score-10-9",
      },
      false: { color: "$gray-secondary-400" },
    },
  } as const,
  defaultVariants: {
    active: false,
  },
});

const ResearchStepStats: React.FC<
  ResearchStepsBase & {
    active: boolean;
  }
> = ({ finished, total, active }) => {
  const { t } = useTranslation("research");
  return (
    <ResearchStepStatsWrapper gap={"$1"} active={active}>
      <Text
        color={"$gray-100"}
        fontFamily={"$body"}
        fontWeight={"$4"}
        fontSize={"$7"}
        lineHeight={"$7"}
      >
        <ResearchStepStatsValue active={active}>
          {finished}
        </ResearchStepStatsValue>
        /{total}
      </Text>

      <Text
        fontFamily={"$body"}
        fontWeight={"$5"}
        fontSize={"$1"}
        lineHeight={"$1"}
        textAlign={"center"}
        maxWidth={100}
      >
        {t(
          "research-list-page.research-card.research-step-card.stats-description",
        )}
      </Text>
    </ResearchStepStatsWrapper>
  );
};
